// app/components/AdvancedChart/hooks/useChartData.ts
import { useState, useEffect } from 'react'
import { Candle } from '../types'
import { getDataSource } from '../utils/dataSource'
import { UTCTimestamp } from 'lightweight-charts'

/**
 * Baby-level explanation:
 * This hook handles fetching symbol data and storing it in state.
 *
 * - initialData: the data initially passed in (from server or props)
 * - symbol: optional symbol string from props
 * - initialTimeframe: default timeframe to use
 */

export function useChartData(
  initialData: Candle[],
  symbol: string | undefined,
  initialTimeframe: string = '1d'
) {
  // local symbol state
  const [currentSymbol, setCurrentSymbol] = useState(() =>
    symbol && symbol.trim() !== '' ? symbol : 'PAYTM.NS'
  )
  const [chartDataLocal, setChartDataLocal] = useState<Candle[]>(
    Array.isArray(initialData) ? initialData.map(candle => ({
      ...candle,
      time: typeof candle.time === 'string' ? Math.floor(new Date(candle.time).getTime() / 1000) as UTCTimestamp : candle.time as UTCTimestamp
    })).sort((a, b) => a.time - b.time) : [] // Sort initial data too
  )
  const [timeframe, setTimeframe] = useState(initialTimeframe)

  // Update currentSymbol when symbol prop changes
  useEffect(() => {
    if (symbol && symbol !== currentSymbol) {
      setCurrentSymbol(symbol)
    }
  }, [symbol])

  // Update timeframe when initialTimeframe prop changes
  useEffect(() => {
    if (initialTimeframe !== timeframe) {
      setTimeframe(initialTimeframe)
    }
  }, [initialTimeframe])

  // Fetch data when symbol or timeframe changes
  useEffect(() => {
    async function fetchAndSetSymbol(sym: string, tf: string) {
      try {
        // Check if symbol ends with .NS, .ns, .BS, or .bs
        const upperSym = sym.toUpperCase()
        if (upperSym.endsWith('.NS') || upperSym.endsWith('.BS')) {
          const response = await fetch(
            `/api/chart?symbol=${sym}&timeframe=${tf}`
          )
          if (!response.ok) {
            throw new Error(`Failed to fetch symbol data for ${sym}`)
          }
          const newData: Candle[] = await response.json();
          // Convert time format and ensure data is sorted by time ascending
          const processedData = newData
            .map(c => ({
              ...c, 
              time: typeof c.time === 'string' ? Math.floor(new Date(c.time).getTime() / 1000) as UTCTimestamp : c.time as UTCTimestamp
            }))
            .sort((a, b) => a.time - b.time); // Sort by time ascending
          setChartDataLocal(processedData);
        } else {
          // For all other symbols (including USDT pairs), use Binance API
          const response = await fetch(
            `https://api.binance.com/api/v3/klines?symbol=${upperSym}&interval=${tf}&limit=1000`
          )
          const data = await response.json()
          const newData: Candle[] = data.map((kline: any) => ({
            time: (kline[0] / 1000) as UTCTimestamp,
            open: parseFloat(kline[1]),
            high: parseFloat(kline[2]),
            low: parseFloat(kline[3]),
            close: parseFloat(kline[4]),
            volume: parseFloat(kline[5])
          }));
          // Binance data should already be sorted, but ensure it just in case
          setChartDataLocal(newData.sort((a, b) => a.time - b.time));
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    // Fetch data when currentSymbol changes, timeframe changes, or initialData is empty
    if (currentSymbol) {
      fetchAndSetSymbol(currentSymbol, timeframe)
    }
  }, [currentSymbol, timeframe])

  return {
    currentSymbol,
    setCurrentSymbol,
    chartDataLocal,
    setChartDataLocal,
    timeframe,
    setTimeframe
  }
}
